<script>
import { GlAlert, GlLoadingIcon } from '@gitlab/ui';
import { uniq, map } from 'lodash';
import blobInfoQuery from 'shared_queries/repository/blob_info.query.graphql';
import CodeFlowFileViewer from 'ee/vue_shared/components/code_flow/code_flow_file_viewer.vue';
import CodeFlowStepsSection from 'ee/vue_shared/components/code_flow/code_flow_steps_section.vue';
import {
  createHighlightSourcesInfo,
  normalizeCodeFlowsInfo,
} from 'ee/vue_shared/components/code_flow/utils/utils';

export default {
  name: 'VulnerabilityCodeFlow',
  components: {
    GlAlert,
    GlLoadingIcon,
    CodeFlowFileViewer,
    CodeFlowStepsSection,
  },
  props: {
    branchRef: {
      type: String,
      required: true,
    },
    details: {
      type: Object,
      required: true,
    },
    projectFullPath: {
      type: String,
      required: true,
      default: '',
    },
    description: {
      type: String,
      required: false,
      default: null,
    },
    descriptionHtml: {
      type: String,
      required: false,
      default: null,
    },
    insideModal: {
      type: Boolean,
      required: false,
      default: false,
    },
    showCodeFlowFileViewer: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      loadingError: false,
      blobInfo: [],
      project: null,
      rawTextBlobs: {},
      highlightSourcesInfo: {},
      modalContentHeight: 0,
      selectedStepNumber: undefined,
    };
  },
  computed: {
    fileNames() {
      const fileNames = map(this.details?.items[0], 'fileLocation.fileName');
      return uniq(fileNames);
    },
    codeSourceFiles() {
      return Object.keys(this.highlightSourcesInfo);
    },
  },
  mounted() {
    if (this.insideModal) {
      this.updateModalContentHeight();
    }
  },
  apollo: {
    project: {
      query: blobInfoQuery,
      variables() {
        return {
          projectPath: this.projectFullPath,
          filePath: this.fileNames,
          ref: this.branchRef,
          refType: null,
          shouldFetchRawText: true,
        };
      },
      update({ project }) {
        const allBlobInfo = project?.repository?.blobs?.nodes || [];
        allBlobInfo.forEach((blobInfo) => {
          const { rawTextBlob, path } = blobInfo;

          this.rawTextBlobs = {
            ...this.rawTextBlobs,
            [path]: rawTextBlob,
          };

          this.blobInfo = {
            ...this.blobInfo,
            [path]: { data: blobInfo },
          };
        });
        const normalizedCodeFlows = normalizeCodeFlowsInfo(this.details);
        this.highlightSourcesInfo = createHighlightSourcesInfo(
          normalizedCodeFlows,
          this.rawTextBlobs,
        );
      },
      error() {
        this.loadingError = true;
      },
    },
  },
  methods: {
    onSelectedStep(value) {
      this.selectedStepNumber = value;
    },
    specificBlobInfo(fileName) {
      return this.blobInfo[fileName]?.data || {};
    },
    updateModalContentHeight() {
      this.$nextTick(() => {
        this.modalContentHeight =
          document
            .querySelector('#security-finding-modal .finding-modal-details')
            ?.getBoundingClientRect().height || 0;
      });
    },
  },
};
</script>

<template>
  <div class="gl-flex gl-gap-5 gl-py-5">
    <gl-loading-icon
      v-if="$apollo.queries.project.loading"
      size="sm"
      class="flex-fill gl-flex gl-items-center gl-justify-center"
    />

    <gl-alert
      v-else-if="loadingError"
      :dismissible="false"
      variant="danger"
      data-testid="loading-error"
    >
      {{ s__('Vulnerability|Something went wrong while trying to get the source file.') }}
    </gl-alert>

    <template v-else>
      <div
        data-testid="code-flow-steps-section-container"
        class="gl-z-0 gl-flex gl-flex-col gl-overflow-auto gl-pl-2 gl-pr-2"
        :class="{ 'gl-w-full': !showCodeFlowFileViewer, 'gl-w-4/10': showCodeFlowFileViewer }"
      >
        <code-flow-steps-section
          ref="codeFlowStepSection"
          :class="{ 'code-flow-content': !insideModal, 'code-flow-content-modal': insideModal }"
          :style="{ '--modal-content-height': `${modalContentHeight}px` }"
          :description="description"
          :description-html="descriptionHtml"
          :details="details"
          :raw-text-blobs="rawTextBlobs"
          @onSelectedStep="onSelectedStep"
        />
      </div>

      <div
        v-if="showCodeFlowFileViewer"
        id="code-flows-container"
        data-testid="code-flows-container"
        class="flex-fill gl-flex gl-w-12/20 gl-flex-col gl-gap-5 gl-overflow-auto gl-pt-2"
        :class="{ 'code-flow-content': !insideModal, 'code-flow-content-modal': insideModal }"
        :style="{ '--modal-content-height': `${modalContentHeight}px` }"
      >
        <code-flow-file-viewer
          v-for="fileName in codeSourceFiles"
          :key="fileName"
          :file-path="fileName"
          :branch-ref="branchRef"
          :hl-info="highlightSourcesInfo[fileName]"
          :blob-info="specificBlobInfo(fileName)"
          :selected-step-number="selectedStepNumber"
        />
      </div>
    </template>
  </div>
</template>
